import React, { useEffect, useRef } from "react"

import { Form, Input, Label, Row, Col, FormFeedback, Alert } from "reactstrap"

import { useFormik } from "formik"
import * as Yup from "yup"
import DematUploadAndFetch from "./dematUploadAndFetch"
import { useUpdateDematDetailsMutation } from "store/api/kyc/kyc"
import Spinners from "components/Common/Spinner"
import toast from "react-hot-toast"
import Select from "react-select"
import { reactSelectStyles } from "helpers/utils"
import { depositories } from "helpers/depositories"
import UploadedFileBox from "./uploadedFileBox"
import { useDispatch, useSelector } from "react-redux"

const defaultValues = {
  cml_report: "",
  demat_account_no: "",
  exchange_type: "NSDl",
  depository_participant_name: "",
}
const DematDetails = ({ kycData }) => {
  const [updateData, { isLoading }] = useUpdateDematDetailsMutation()
  const { user } = useSelector((state) => state.user)
  const dispatch = useDispatch()
  const lastClicked = useRef(null)

  const validation = useFormik({
    initialValues: { ...defaultValues },
    validationSchema: Yup.object({}),
    validateOnMount: false,
    onSubmit: async (values) => {
      const buttonName = lastClicked.current

      const params = {
        ...values,
        depository_participant_name: values?.depository_participant_name?.value,
        kycStep: kycData.status,
        skip: "NO",
      }

      if (buttonName === "skip") {
        params["skip"] = "YES"
      }
      onFormUpdate(params)
    },
  })

  useEffect(() => {
    const params = {
      demat_account_no: kycData?.investorsDematDetails?.demat_account_no,
      exchange_type: kycData?.investorsDematDetails?.exchange_type || "NSDL",
    }
    if (kycData?.investorsDematDetails) {
      params["depository_participant_name"] = {
        label: kycData?.investorsDematDetails?.depository_participant_name,
        value: kycData?.investorsDematDetails?.depository_participant_name,
      }
    }
    validation.setValues({ ...params })
  }, [kycData])

  useEffect(() => {
    if (validation.values.demat_account_no) {
      const val = validation.values.demat_account_no
      if (val.toLowerCase().startsWith("in")) {
        validation.setFieldValue("exchange_type", "NSDL")
      } else if (val.toLowerCase().startsWith("12")) {
        validation.setFieldValue("exchange_type", "CDSL")
      }
    }
  }, [validation.values.demat_account_no])

  const onFormUpdate = async (values) => {
    try {
      const { data, message, success } = await updateData({
        ...values,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "toastMessage" })
        window.location.reload()
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const checkSkipVisibility = () => {
    if (user) {
      const arr = user?.preference
      const showButton =
        arr.includes("ICD") && !arr.includes("BOND") && !arr.includes("REIT")
      return showButton
    }
    return false
  }

  return (
    <>
      <div className="bank-details-wrapper">
        <h5 className="title mb-3">Demat Details </h5>
        <Form
          onSubmit={(e) => {
            e.preventDefault()
            validation.handleSubmit(e)
          }}
        >
          <Row className="mb-2">
            <Col xl={12}>
              <Label className="col-form-label">Upload CML</Label>

              <DematUploadAndFetch
                fieldKey={"cml_report"}
                validation={validation}
                extensionAllowedInput={".pdf"}
                extensionAllowed={["application/pdf"]}
              />
              {kycData?.investorsDematDetails?.cml_report_url && (
                <UploadedFileBox
                  label={"CML Report"}
                  href={kycData?.investorsDematDetails?.cml_report_url}
                />
              )}
            </Col>

            <Col xl={12}>
              <Label className="col-form-label">Demat Account No.</Label>
              <Input
                name="demat_account_no"
                type="text"
                className="form-control"
                placeholder="16 Digit demat number"
                value={validation.values.demat_account_no}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.demat_account_no &&
                  validation.errors.demat_account_no
                    ? true
                    : false
                }
              />
            </Col>

            {validation.touched.demat_account_no &&
            validation.errors.demat_account_no ? (
              <FormFeedback type="invalid">
                {validation.errors.demat_account_no}
              </FormFeedback>
            ) : null}

            <Col xl={12}>
              <Label className="col-form-label">Exchange Type</Label>
              <select
                name="exchange_type"
                className={`form-select pageSize ${
                  validation.touched.exchange_type &&
                  validation.errors.exchange_type &&
                  "is-invalid"
                }`}
                disabled
                value={validation.values.exchange_type}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              >
                <option value="NSDL"> NSDL</option>
                <option value="CDSL"> CDSL</option>
              </select>
            </Col>

            {validation.touched.exchange_type &&
            validation.errors.exchange_type ? (
              <FormFeedback type="invalid">
                {validation.errors.exchange_type}
              </FormFeedback>
            ) : null}
            <Col xl={12}>
              <Label className="col-form-label">
                Depository Participant Name
              </Label>
              <Select
                styles={reactSelectStyles}
                options={depositories}
                placeholder="Select Name"
                value={validation.values.depository_participant_name}
                onChange={(item) =>
                  validation.setFieldValue("depository_participant_name", item)
                }
              />
            </Col>
            {validation.errors.depository_participant_name ? (
              <FormFeedback type="invalid">
                {validation.errors.depository_participant_name}
              </FormFeedback>
            ) : null}

            <Col sm={12}>
              <div className="d-flex flex-column align-items-center mt-4">
                <button
                  type="submit"
                  onClick={() => (lastClicked.current = "submit")}
                  className="btn btn-primary w-100"
                >
                  {isLoading ? <Spinners /> : "Continue"}
                </button>
                {checkSkipVisibility() && (
                  <button
                    type="submit"
                    onClick={() => (lastClicked.current = "skip")}
                    style={{
                      fontSize: "13px",
                      marginTop: "10px",
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      color: "#035ca1",
                      cursor: "pointer",
                      fontSize: "15px",
                      fontWeight: "bold",
                      marginTop: "10px",
                    }}
                  >
                    Skip
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  )
}

export default DematDetails
